"use client";
import { Sport, Standings } from "common";
import React from "react";
import Table, { ColumnDef } from "../primitives/table";
import TeamDisplay from "./display";
import { empty } from "@/utils";
import { removeLeadingZeroDecimal } from "@/utils";

export default function TeamStandingsTable({
  data,
  sport,
  header,
}: {
  data: Standings[];
  sport: Sport;
  header?: string;
}) {
  const columns: ColumnDef<Standings>[] = React.useMemo(
    () => [
      {
        id: "team",
        header: header,
        prependOrder: true,
        meta: {
          classNames:
            "bg-surface sticky left-0 z-[1] min-w-[124px] w-[124px] max-w-[124px] md:min-w-[260px] md:w-[260px] md:max-w-[260px]",
        },
        cell: (info) => {
          const row = info.row.original;
          return (
            <>
              <div className="hidden md:block">
                <TeamDisplay
                  sport={sport}
                  size="xs"
                  variant="location-name"
                  team={{
                    id: row.id,
                    name: row.name,
                    location: row.location,
                    abbreviation: row.abbreviation,
                    logoUrl: row.logoUrl,
                  }}
                />
              </div>
              <div className="block md:hidden">
              <TeamDisplay
                  sport={sport}
                  size="xs"
                  variant="abbreviation"
                  team={{
                    id: row.id,
                    name: row.name,
                    location: row.location,
                    abbreviation: row.abbreviation,
                    logoUrl: row.logoUrl,
                  }}
                />
              </div>
            </>
          );
        },
      },
      ...(sport !== "nhl"
        ? [
            {
              header: "win",
              accessorKey: "wins",
              maxWidth: "60px",
            },
            {
              header: "loss",
              accessorKey: "losses",
              maxWidth: "60px",
            },
            ...(sport !== "nba" && sport !== "mlb"
              ? [
                  {
                    header: "tie",
                    accessorKey: "ties",
                    maxWidth: "60px",
                  },
                ]
              : []),
          ]
        : [
            {
              header: "gp",
              accessorKey: "gamesPlayed",
              maxWidth: "50px",
            },
            {
              header: "win",
              accessorKey: "wins",
              maxWidth: "60px",
            },
            {
              header: "loss",
              accessorKey: "losses",
              maxWidth: "60px",
            },
            {
              header: "ot",
              accessorKey: "overtimeLosses",
              maxWidth: "60px",
            },
          ]),
      ...(sport !== "nhl"
        ? [
            {
              header: "pct",
              // accessorKey: "pct",
              maxWidth: "50px",
              accessorFn: (info: { pct: number }) => {
                return removeLeadingZeroDecimal(info.pct);
              },
            },
          ]
        : [
            {
              header: "pts",
              accessorKey: "points",
            },
          ]),
      {
        header: "ats",
        enableSorting: false,
        accessorFn: (info) => {
          if (
            info.againstSpread.wins > 0 ||
            info.againstSpread.losses > 0 ||
            info.againstSpread.ties > 0
          ) {
            return `${info.againstSpread.wins || 0}-${
              info.againstSpread.losses || 0
            }-${info.againstSpread.ties || 0}`;
          } else {
            return empty();
          }
        },
      },
      {
        id: "total",
        header: () => {
          return <div className="flex min-w-[53px]">Total (O/U)</div>;
        },
        enableSorting: false,
        accessorFn: (info) => {
          if (
            info.overUnders.wins > 0 ||
            info.overUnders.losses > 0 ||
            info.overUnders.ties > 0
          ) {
            return `${info.overUnders.wins || 0}-${
              info.overUnders.losses || 0
            }-${info.overUnders.ties || 0}`;
          } else {
            return empty();
          }
        },
      },

      {
        header: "home",
        enableSorting: false,
        accessorFn: (info) => {
          if (
            info.homeRecord &&
            (info.homeRecord.wins > 0 ||
              info.homeRecord.losses > 0 ||
              info.homeRecord.ties > 0)
          ) {
            return `${info.homeRecord.wins || 0}-${
              info.homeRecord.losses || 0
            }-${info.homeRecord.ties || 0}`;
          } else {
            return empty();
          }
        },
      },
      {
        header: "away",
        enableSorting: false,
        accessorFn: (info) => {
          if (
            info.awayRecord &&
            (info.awayRecord.wins > 0 ||
              info.awayRecord.losses > 0 ||
              info.awayRecord.ties > 0)
          ) {
            return `${info.awayRecord.wins || 0}-${
              info.awayRecord.losses || 0
            }-${info.awayRecord.ties || 0}`;
          } else {
            return empty();
          }
        },
      },
      ...(sport !== "mlb"
        ? [
            {
              header: "Conf",
              enableSorting: false,
              accessorFn: (info: {
                conferenceRecord: {
                  wins: number;
                  losses: number;
                  ties: number;
                };
              }) => {
                if (
                  info.conferenceRecord.wins > 0 ||
                  info.conferenceRecord.losses > 0 ||
                  info.conferenceRecord.ties > 0
                ) {
                  return `${info.conferenceRecord.wins || 0}-${
                    info.conferenceRecord.losses || 0
                  }-${info.conferenceRecord.ties || 0}`;
                } else {
                  return empty();
                }
              },
            },
            {
              id: "non-conf",
              header: () => {
                return <div className="flex min-w-[48px]">Non Conf</div>;
              },
              enableSorting: false,
              accessorFn: (info: {
                nonConfRecord: { wins: number; losses: number; ties: number };
              }) => {
                if (
                  info.nonConfRecord &&
                  (info.nonConfRecord.wins > 0 ||
                    info.nonConfRecord.losses > 0 ||
                    info.nonConfRecord.ties > 0)
                ) {
                  return `${info.nonConfRecord.wins || 0}-${
                    info.nonConfRecord.losses || 0
                  }-${info.nonConfRecord.ties || 0}`;
                } else {
                  return empty();
                }
              },
            },
          ]
        : []),

      {
        header: "div",
        enableSorting: false,
        accessorFn: (info) => {
          if (
            info.divisionRecord.wins > 0 ||
            info.divisionRecord.losses > 0 ||
            info.divisionRecord.ties > 0
          ) {
            return `${info.divisionRecord.wins || 0}-${
              info.divisionRecord.losses || 0
            }-${info.divisionRecord.ties || 0}`;
          } else {
            return empty();
          }
        },
      },
      {
        header: "l10",
        enableSorting: false,
        accessorFn: (info) => {
          if (sport === "nhl") {
            return `${info.last10Record.wins}-${info.last10Record.losses}-${info.last10Record.overtimeLosses}`;
          } else {
            return `${info.last10Record.wins}-${info.last10Record.losses}-${info.last10Record.ties}`;
          }
        },
      },
      {
        header: "strk",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          if (row.streak) {
            return (
              <div className="flex items-center gap-1">
                <span className="text-title-sm-medium">
                  {row.streak}
                </span>
              </div>
            );
          } else {
            return empty();
          }
        },
      },
    ],
    []
  );
  return (
    <Table
      type="banded"
      columns={columns}
      data={data}
      initialSort={{column: sport === "nhl" ? "points" : "wins", direction: "desc"}}
      scrollHint={{show: true, left: 124}}
    />
  );
}
